// "Add to calendar" for a single event: a Google Calendar template URL, and an
// .ics file for Apple Calendar (which is also what Outlook opens).
//
// TIMEZONE. lyh_events.start_time / end_time are `timestamptz`, so the string the
// API returns is an absolute instant with an offset already applied - a 7pm
// karaoke in Lynchburg comes back as 2026-07-15T23:00:00+00:00. That makes the
// UTC form the exact, lossless one to emit, and it needs no VTIMEZONE block:
// every calendar app converts it back to whatever timezone the reader is in, the
// same way the event cards already render times in the browser's local zone. A
// TZID of America/New_York would be no more correct here and would require
// shipping the full DST rules alongside it to stay valid.

import type { EventRow } from '../types'

/** Only the fields an entry needs, so the builders take a plain object rather
    than a whole row. */
export type CalendarEvent = Pick<
  EventRow,
  'id' | 'name' | 'start_time' | 'end_time' | 'venue' | 'note' | 'link'
>

// Most listings carry no end time. Two hours is long enough to cover a trivia
// night or a market run without blocking out someone's whole evening.
const DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000

const UID_DOMAIN = 'calendar.lyhevents.com'
const PRODID = '-//LYH Events//Community Calendar//EN'

/** end_time when there is a usable one, otherwise start + 2h. An end that lands
    on or before the start is bad data, and treating it as real would hand the
    calendar a negative-length event. */
function endOf(event: CalendarEvent): Date {
  const start = new Date(event.start_time)
  if (event.end_time) {
    const end = new Date(event.end_time)
    if (end.getTime() > start.getTime()) return end
  }
  return new Date(start.getTime() + DEFAULT_DURATION_MS)
}

/** RFC 5545 UTC date-time: 20260715T230000Z. */
function toUtcStamp(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')
}

/** What goes in the description. The note is what tells you whether you want to
    go; the link is where you go to find out more, so it sits under it. */
function describe(event: CalendarEvent): string {
  return [event.note, event.link].filter(Boolean).join('\n\n')
}

export function googleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.name,
    dates: `${toUtcStamp(new Date(event.start_time))}/${toUtcStamp(endOf(event))}`,
  })
  if (event.venue) params.set('location', event.venue)
  const details = describe(event)
  if (details) params.set('details', details)
  return `https://calendar.google.com/calendar/render?${params}`
}

/** Escape a TEXT value (RFC 5545 §3.3.11). Backslash goes first, or it would
    escape the backslashes the later rules add. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n')
}

/** RFC 5545 §3.1: a content line runs to 75 OCTETS, and the rest continues on
    the next line behind a single space. Measured in bytes rather than
    characters, because one curly quote in a note spends three of the 75. */
function fold(line: string): string {
  const encoder = new TextEncoder()
  const parts: string[] = []
  let current = ''
  let bytes = 0
  // Iterating the string yields whole code points, so an emoji in an event name
  // is never split down the middle.
  for (const ch of line) {
    const size = encoder.encode(ch).length
    // A continuation line spends one of its octets on the leading space.
    const limit = parts.length === 0 ? 75 : 74
    if (bytes + size > limit) {
      parts.push(current)
      current = ''
      bytes = 0
    }
    current += ch
    bytes += size
  }
  parts.push(current)
  return parts.join('\r\n ')
}

export function buildIcs(event: CalendarEvent): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:${PRODID}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@${UID_DOMAIN}`,
    `DTSTAMP:${toUtcStamp(new Date())}`,
    `DTSTART:${toUtcStamp(new Date(event.start_time))}`,
    `DTEND:${toUtcStamp(endOf(event))}`,
    `SUMMARY:${escapeText(event.name)}`,
  ]
  // venue, note and link are all nullable. An empty LOCATION or DESCRIPTION is
  // valid but shows up as a blank field in Apple Calendar, so leave the property
  // out entirely when there is nothing to put in it.
  if (event.venue) lines.push(`LOCATION:${escapeText(event.venue)}`)
  const details = describe(event)
  if (details) lines.push(`DESCRIPTION:${escapeText(details)}`)
  // A URI value, not TEXT: it carries no backslash escaping. Apple Calendar
  // renders it as a clickable field of its own.
  if (event.link) lines.push(`URL:${event.link}`)
  lines.push('END:VEVENT', 'END:VCALENDAR')
  return `${lines.map(fold).join('\r\n')}\r\n`
}

export function icsFileName(event: CalendarEvent): string {
  const slug = event.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 60)
    .replace(/^-+|-+$/g, '')
  return `${slug || 'event'}.ics`
}

export function downloadIcs(event: CalendarEvent): void {
  const blob = new Blob([buildIcs(event)], {
    type: 'text/calendar;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = icsFileName(event)
  // Firefox only acts on a click if the anchor is in the document.
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoking in the same tick cancels the save in some browsers; one turn of the
  // event loop is enough for it to have started. On iOS the file lands in Files
  // and the reader taps it to hand it to Calendar, which is the platform's own
  // flow for an .ics rather than something to work around.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
